import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  Loader2,
  Plus,
  Server,
  Settings,
  Power,
  Trash2,
  RefreshCw,
  ActivitySquare,
  CheckCircle,
  XCircle,
  Clock,
  Cpu,
} from 'lucide-react';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  fetchSharedServers,
  fetchSharedServerQueue,
  createSharedServer,
  toggleSharedServer,
  initializeSharedServer,
  testSharedServerConnection,
  deleteSharedServer,
  fetchSharedServerStats,
} from '@/api/admin';
import type { SharedServer } from '@/types/admin';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

function statusBadge(server: SharedServer) {
  const status = server.status || 'UNKNOWN';
  if (status === 'READY') {
    return (
      <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
        <CheckCircle className="h-3 w-3 mr-1" />
        {status}
      </Badge>
    );
  }
  if (status.toLowerCase().includes('error')) {
    return (
      <Badge variant="destructive">
        <XCircle className="h-3 w-3 mr-1" />
        {status}
      </Badge>
    );
  }
  return (
    <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
      <Clock className="h-3 w-3 mr-1" />
      {status}
    </Badge>
  );
}

export function SharedServersPage() {
  const queryClient = useQueryClient();
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [form, setForm] = useState({
    server_name: '',
    ip_address: '',
    username: 'root',
    password: '',
    port: 22,
    run_server_init: false,
  });

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['admin-shared-servers'],
    queryFn: fetchSharedServers,
    refetchInterval: autoRefresh ? 10000 : false,
  });

  const { data: queueData } = useQuery({
    queryKey: ['admin-shared-servers-queue'],
    queryFn: fetchSharedServerQueue,
    refetchInterval: autoRefresh ? 10000 : false,
  });

  const { data: statsData } = useQuery({
    queryKey: ['admin-shared-servers-stats'],
    queryFn: fetchSharedServerStats,
    refetchInterval: autoRefresh ? 10000 : false,
  });

  const createMutation = useMutation({
    mutationFn: createSharedServer,
    onSuccess: () => {
      toast.success('Shared server created');
      queryClient.invalidateQueries({ queryKey: ['admin-shared-servers'] });
      setCreateDialogOpen(false);
      setForm({
        server_name: '',
        ip_address: '',
        username: 'root',
        password: '',
        port: 22,
        run_server_init: false,
      });
    },
    onError: (err: any) => toast.error(err.message || 'Failed to create server'),
  });

  const toggleMutation = useMutation({
    mutationFn: (id: number) => toggleSharedServer(id),
    onSuccess: () => {
      toast.success('Availability toggled');
      queryClient.invalidateQueries({ queryKey: ['admin-shared-servers'] });
    },
    onError: (err: any) => toast.error(err.message || 'Failed to toggle'),
  });

  const initMutation = useMutation({
    mutationFn: (id: number) => initializeSharedServer(id),
    onSuccess: () => {
      toast.success('Initialization started');
      queryClient.invalidateQueries({ queryKey: ['admin-shared-servers'] });
    },
    onError: (err: any) => toast.error(err.message || 'Failed to initialize'),
  });

  const testMutation = useMutation({
    mutationFn: (id: number) => testSharedServerConnection(id),
    onSuccess: () => toast.success('Connection OK'),
    onError: (err: any) => toast.error(err.message || 'Connection failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteSharedServer(id),
    onSuccess: () => {
      toast.success('Server deactivated');
      queryClient.invalidateQueries({ queryKey: ['admin-shared-servers'] });
    },
    onError: (err: any) => toast.error(err.message || 'Failed to delete'),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate({
      server_name: form.server_name,
      ip_address: form.ip_address,
      username: form.username,
      password: form.password,
      port: form.port,
      run_server_init: form.run_server_init,
    });
  };

  const handleToggle = (server: SharedServer) => {
    const action = server.available ? 'disable' : 'enable';
    if (confirm(`Are you sure you want to ${action} "${server.name || server.ip_address}"?`)) {
      toggleMutation.mutate(server.id);
    }
  };

  const handleInit = (server: SharedServer) => {
    if (confirm(`Initialize server "${server.name || server.ip_address}"? This will run the init playbook.`)) {
      initMutation.mutate(server.id);
    }
  };

  const handleDelete = (server: SharedServer) => {
    if (confirm(`Deactivate server "${server.name || server.ip_address}"? This action cannot be undone.`)) {
      deleteMutation.mutate(server.id);
    }
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>Failed to load shared servers.</AlertDescription>
        </Alert>
      </AdminLayout>
    );
  }

  const servers = data?.servers || [];
  const summary = data?.summary_stats;

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Shared Servers</h1>
          <p className="text-sm text-muted-foreground">Manage shared dev servers and queue</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Switch
              id="auto-refresh"
              checked={autoRefresh}
              onCheckedChange={setAutoRefresh}
            />
            <Label htmlFor="auto-refresh" className="text-sm">Auto-refresh</Label>
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Server
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Server className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Servers</p>
                <p className="text-2xl font-semibold">{summary?.total_shared_servers || servers.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Active</p>
                <p className="text-2xl font-semibold">{summary?.active_servers || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Cpu className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Active Builds</p>
                <p className="text-2xl font-semibold">{summary?.total_active_builds || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">24h Activity</p>
                <p className="text-2xl font-semibold">{summary?.recent_activity_24h || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Servers Table */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Server className="h-5 w-5" />
                Servers ({servers.length})
              </CardTitle>
              <Link to="/admin/shared-servers/queue" className="text-sm text-primary hover:underline">
                View full queue
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {servers.length ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-muted-foreground border-b">
                      <th className="py-3 px-2 font-medium">Server</th>
                      <th className="py-3 px-2 font-medium">Status</th>
                      <th className="py-3 px-2 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {servers.map(({ server }) => (
                      <tr key={server.id} className="border-b hover:bg-muted/50">
                        <td className="py-3 px-2">
                          <div>
                            <span className="font-medium">{server.name || server.ip_address}</span>
                            <div className="text-xs text-muted-foreground">
                              {server.ip_address}:{server.port}
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-2">
                          <div className="flex flex-col gap-1">
                            {statusBadge(server)}
                            {server.available ? (
                              <Badge variant="secondary" className="w-fit">Available</Badge>
                            ) : (
                              <Badge variant="outline" className="w-fit">Unavailable</Badge>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-2">
                          <div className="flex flex-wrap gap-1">
                            <Link to={`/admin/shared-servers/${server.id}`}>
                              <Button size="sm" variant="outline" title="View details">
                                Details
                              </Button>
                            </Link>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleToggle(server)}
                              disabled={toggleMutation.isPending}
                              title={server.available ? 'Disable server' : 'Enable server'}
                            >
                              <Power className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => testMutation.mutate(server.id)}
                              disabled={testMutation.isPending}
                              title="Test connection"
                            >
                              <ActivitySquare className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleInit(server)}
                              disabled={initMutation.isPending}
                              title="Initialize server"
                            >
                              <RefreshCw className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDelete(server)}
                              disabled={deleteMutation.isPending}
                              title="Deactivate server"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <Server className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No shared servers yet</p>
                <Button className="mt-4" onClick={() => setCreateDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add your first server
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Right Sidebar */}
        <div className="space-y-6">
          {/* Queue Preview */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-sm font-medium">
                <Settings className="h-4 w-4" />
                Queue ({queueData?.queue_items?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {queueData?.queue_items?.length ? (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {queueData.queue_items.slice(0, 5).map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between text-sm p-2 rounded bg-muted/50"
                    >
                      <span>Build #{item.build_id}</span>
                      <Badge variant="outline">{item.status}</Badge>
                    </div>
                  ))}
                  {queueData.queue_items.length > 5 && (
                    <Link
                      to="/admin/shared-servers/queue"
                      className="block text-center text-sm text-primary hover:underline"
                    >
                      View all {queueData.queue_items.length} items
                    </Link>
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">Queue is empty</p>
              )}
            </CardContent>
          </Card>

          {/* Live Stats */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-sm font-medium">
                <Power className="h-4 w-4" />
                Live Stats
              </CardTitle>
            </CardHeader>
            <CardContent>
              {statsData ? (
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Total servers</span>
                    <span className="font-semibold">{statsData.summary.total_servers}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Available</span>
                    <span className="font-semibold">{statsData.summary.available_servers}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Active builds</span>
                    <span className="font-semibold">{statsData.summary.total_active_builds}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Builds queued</span>
                    <span className="font-semibold">{statsData.summary.builds_queued}</span>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">No stats available</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Create Server Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Shared Server</DialogTitle>
            <DialogDescription>
              Add a new server to the shared server pool
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="server_name">Server Name</Label>
              <Input
                id="server_name"
                placeholder="e.g., dev-server-1"
                value={form.server_name}
                onChange={(e) => setForm((f) => ({ ...f, server_name: e.target.value }))}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="ip_address">IP Address *</Label>
                <Input
                  id="ip_address"
                  placeholder="e.g., 192.168.1.100"
                  value={form.ip_address}
                  onChange={(e) => setForm((f) => ({ ...f, ip_address: e.target.value }))}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="port">SSH Port</Label>
                <Input
                  id="port"
                  type="number"
                  placeholder="22"
                  value={form.port}
                  onChange={(e) => setForm((f) => ({ ...f, port: Number(e.target.value) }))}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  placeholder="root"
                  value={form.username}
                  onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="SSH password"
                  value={form.password}
                  onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                />
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="run_server_init"
                checked={form.run_server_init}
                onCheckedChange={(checked) =>
                  setForm((f) => ({ ...f, run_server_init: checked === true }))
                }
              />
              <Label htmlFor="run_server_init" className="text-sm font-normal">
                Run server initialization playbook after creation
              </Label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createMutation.isPending || !form.ip_address}>
                {createMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Server
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
